import { Building2, Crown, BadgeCheck } from "lucide-react";
import { useFileUrl } from "@/lib/api";
import { SPECS, AVAILABILITY, APP_STATUS, certState, isVip } from "@/lib/labels";

export const VipBadge = ({ user, testId }) => (
  <>
    {user?.verified && <BadgeCheck size={17} className="text-sky-600 shrink-0" aria-label="Verified" data-testid={testId ? `${testId}-verified` : undefined} />}
    {isVip(user) && (
      <span data-testid={testId} title="VIP member" className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-400 text-slate-950 font-mono text-[10px] font-bold uppercase tracking-wide"><Crown size={11} />VIP</span>
    )}
  </>
);

export function UserAvatar({ user, size = 40, testId }) {
  const url = useFileUrl(user?.avatar_path);
  const initials = (user?.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  const isCompany = user?.role === "company";
  const style = { width: size, height: size, fontSize: size * 0.36 };
  return (
    <div data-testid={testId} style={style}
      className={`shrink-0 overflow-hidden flex items-center justify-center font-semibold ${isCompany ? "rounded-md bg-slate-900 text-amber-400" : "rounded-full bg-sky-100 text-sky-800"}`}>
      {url ? <img src={url} alt={user?.name} className="w-full h-full object-cover" />
        : isCompany && !initials ? <Building2 size={size * 0.5} /> : initials}
    </div>
  );
}

export const Pill = ({ children, cls = "bg-slate-100 text-slate-700 border-slate-200", testId }) => (
  <span data-testid={testId} className={`inline-flex items-center gap-1 border px-2 py-0.5 rounded font-mono text-[11px] uppercase tracking-wide ${cls}`}>{children}</span>
);

export const SpecPill = ({ spec }) => <Pill cls="bg-sky-50 text-sky-800 border-sky-200">{SPECS[spec] || spec}</Pill>;

export const AvailabilityPill = ({ value }) => value && AVAILABILITY[value]
  ? <Pill cls={AVAILABILITY[value].cls}>{AVAILABILITY[value].label}</Pill> : null;

export const StatusPill = ({ status, testId }) => <Pill testId={testId} cls={APP_STATUS[status]?.cls}>{APP_STATUS[status]?.label}</Pill>;

export function CertList({ certs = [] }) {
  if (!certs.length) return <p className="text-sm text-slate-500">No certificates added.</p>;
  return (
    <ul className="divide-y divide-slate-100">
      {certs.map((c, i) => {
        const st = certState(c.expires);
        return (
          <li key={`${c.name}-${c.issued}-${i}`} data-testid={`cert-item-${i}`} className="py-2.5 flex flex-wrap items-center justify-between gap-2">
            <span className="font-medium text-slate-800 text-sm">{c.name}</span>
            <Pill cls={st.cls}>{st.label}</Pill>
          </li>
        );
      })}
    </ul>
  );
}

export const Loader = () => (
  <div data-testid="page-loader" className="flex items-center justify-center py-24">
    <svg width="44" height="44" viewBox="0 0 44 44" className="rotor text-sky-600">
      {[0, 120, 240].map((r) => <path key={r} d="M22 22 L24 4 Q22 2 20 4 Z" fill="currentColor" transform={`rotate(${r} 22 22)`} />)}
      <circle cx="22" cy="22" r="3" fill="#F59E0B" />
    </svg>
  </div>
);

export const EmptyState = ({ title, text, action, testId }) => (
  <div data-testid={testId} className="panel border-dashed p-10 text-center">
    <p className="font-semibold text-slate-800">{title}</p>
    {text && <p className="text-sm text-slate-500 mt-1">{text}</p>}
    {action && <div className="mt-4">{action}</div>}
  </div>
);
